package songwritersnotebook.app.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import songwritersnotebook.app.utils.ChordUtils
import songwritersnotebook.app.viewmodels.ChordCreatorUiState
import songwritersnotebook.app.tutorial.TutorialOverlay
import songwritersnotebook.app.tutorial.TutorialStep
import songwritersnotebook.app.tutorial.tutorialTarget
import songwritersnotebook.app.adaptiveSheetWidth

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun ChordCreatorSheet(
    // Data states
    initialChordName: String?,
    chordCreatorUiState: ChordCreatorUiState,
    
    // Event callbacks
    onDismiss: () -> Unit,
    onSave: (String) -> Unit,
    onPlayCurrentChord: () -> Unit,
    onShuffleChord: () -> Unit,
    onPresetSelected: (String, String) -> Unit,
    onSelectQualityCategory: (Int) -> Unit,
    onPlayPreset: (String, String) -> Unit,
    fretWindowStart: Int,
    onFretWindowChange: (Int) -> Unit,
    onOctaveChange: (Int) -> Unit,
    onClearManualKeys: () -> Unit,
    onToggleManualKey: (String) -> Unit,
    onTabChange: (List<Any>) -> Unit,
    onTuningChange: (Int, Int) -> Unit,
    onCapoChange: (Int) -> Unit,
    onResetTuning: () -> Unit,
    onCustomToggle: () -> Unit,
    onInit: (String?) -> Unit,
    isFavorited: Boolean,
    onToggleFavorite: (songwritersnotebook.app.data.ChordDefinition) -> Unit,
    isLeftHanded: Boolean = false
) {
    var customChordName by remember { mutableStateOf(initialChordName ?: "C") }
    var showTuningPopup by remember { mutableStateOf(false) }
    var showCapoPopup by remember { mutableStateOf(false) }

    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)

    val displayNotesOnKeyboard = remember(
        chordCreatorUiState.isCustom,
        chordCreatorUiState.manualKeys,
        chordCreatorUiState.selectedInstrument,
        chordCreatorUiState.selectedChordRoot,
        chordCreatorUiState.selectedChordQuality
    ) {
        if (chordCreatorUiState.isCustom) {
            // If it's a custom chord, show the notes the user has tapped.
            chordCreatorUiState.manualKeys
        } else {
            // If it's a PRESET, ignore manualKeys and show the correct piano voicing.
            val chordName = ChordUtils.getChordName(
                chordCreatorUiState.selectedChordRoot,
                chordCreatorUiState.selectedChordQuality
            )
            // This variable is only used for the piano, so we *always* get the piano voicing.
            ChordUtils.PIANO_CHORD_VOICINGS[chordName] ?: emptyList()
        }
    }

    LaunchedEffect(initialChordName) {
        // onInit(initialChordName) - Removed to prevent re-initialization override
        customChordName = initialChordName ?: "C"
    }

    LaunchedEffect(
        chordCreatorUiState.selectedChordRoot,
        chordCreatorUiState.selectedChordQuality,
        chordCreatorUiState.isCustom // Depend on the isCustom flag
    ) {
        // If the chord is NOT custom (meaning it's a preset),
        // then update the name field to match the preset.
        if (!chordCreatorUiState.isCustom) {
            customChordName = ChordUtils.getChordName(
                chordCreatorUiState.selectedChordRoot,
                chordCreatorUiState.selectedChordQuality
            )
        }
    }

    LaunchedEffect(
        chordCreatorUiState.manualKeys,
        chordCreatorUiState.currentTab,
        chordCreatorUiState.isCustom,
        chordCreatorUiState.selectedInstrument
    ) {
        // This runs when creating a new chord (initialChordName == null)
        // AND the ViewModel has determined the state is custom (isCustom == true)
        if (initialChordName == null && chordCreatorUiState.isCustom) {
            var detectedName: String? = null
            var notes: List<String> = emptyList()

            if (chordCreatorUiState.selectedInstrument == "piano" && chordCreatorUiState.manualKeys.isNotEmpty()) {
                notes = chordCreatorUiState.manualKeys
            } else if (chordCreatorUiState.selectedInstrument == "guitar") {
                notes = ChordUtils.tabToNotes(
                    chordCreatorUiState.currentTab,
                    chordCreatorUiState.guitarTuning,
                    chordCreatorUiState.guitarCapo
                )
            }

            if (notes.isNotEmpty()) {
                val matchingChord = ChordUtils.detectChordFromNotes(notes, null)

                if (matchingChord != null) {
                    // A standard chord was found (e.g., Caug)
                    detectedName = matchingChord.name
                } else {
                    // No standard chord found, create a "Custom" placeholder
                    // Get the root of the first note played.
                    val rootNoteName = ChordUtils.ROOT_NOTES[ChordUtils.noteNameToMidi(notes[0])!! % 12]
                    detectedName = "$rootNoteName Custom"
                }
            }

            if (detectedName != null) {
                customChordName = detectedName
            }
        }
    }



    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
    ) {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
            Column(
                modifier = Modifier
                    .adaptiveSheetWidth()
                    .navigationBarsPadding()
            ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 16.dp, end = 16.dp, top = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween // Changed to SpaceBetween
            ) {
                Text(
                    if (initialChordName != null) "Edit Chord" else "Create Chord",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                OutlinedIconButton(
                    onClick = onPlayCurrentChord,
                    modifier = Modifier.size(40.dp)
                ) {
                    Icon(Icons.Default.PlayArrow, "Play Chord")
                }
                OutlinedIconButton(
                    onClick = onShuffleChord,
                    modifier = Modifier.size(40.dp).tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_SHUFFLE)
                ) {
                    Icon(Icons.Default.Shuffle, "Shuffle Chord")
                }
                TextButton(
                    onClick = onCustomToggle,
                    colors = ButtonDefaults.textButtonColors(
                        contentColor = if (chordCreatorUiState.isCustom) MaterialTheme.colorScheme.primary else Color.Gray
                    ),
                    modifier = Modifier
                        .height(40.dp)
                        .tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_CUSTOM_TOGGLE)
                ) {
                    Text(
                        "Custom",
                        fontWeight = if (chordCreatorUiState.isCustom) FontWeight.Bold else FontWeight.Normal
                    )
                }
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Close")
                }
            }

            Column(
                modifier = Modifier
                    .weight(1f, fill = false)
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 16.dp)
            ) {
                Spacer(modifier = Modifier.height(16.dp))

                Text("Root Note", style = MaterialTheme.typography.titleMedium)
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier
                        .fillMaxWidth()
                        .tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_VOICING)
                ) {
                    ChordUtils.ROOT_NOTES.forEach { note ->
                        val isSelected = chordCreatorUiState.selectedChordRoot == note
                        Button(
                            onClick = {
                                onPresetSelected(note, chordCreatorUiState.selectedChordQuality)
                                onPlayPreset(note, chordCreatorUiState.selectedChordQuality)
                            },
                            shape = RoundedCornerShape(8.dp),
                            contentPadding = PaddingValues(0.dp),
                            modifier = Modifier.size(48.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = if (isSelected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceVariant,
                                contentColor = if (isSelected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        ) {
                            Text(note, fontFamily = FontFamily.Monospace, softWrap = false)
                        }
                    }
                }

                Text(
                    "Quality",
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.padding(top = 16.dp)
                )

                // 1. The Category Tabs
                TabRow(
                    selectedTabIndex = chordCreatorUiState.selectedQualityCategoryIndex,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 8.dp),
                    containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f),
                    contentColor = MaterialTheme.colorScheme.primary
                ) {
                    ChordUtils.CHORD_QUALITY_CATEGORIES.forEachIndexed { index, category ->
                        Tab(
                            selected = chordCreatorUiState.selectedQualityCategoryIndex == index,
                            onClick = { onSelectQualityCategory(index) },
                            text = { Text(category.name, fontSize = 12.sp) }
                        )
                    }
                }

                // 2. The Quality Buttons for the selected tab
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 16.dp)
                        .tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_VOICING, isSecondary = true)
                ) {
                    val selectedCategory = ChordUtils.CHORD_QUALITY_CATEGORIES[chordCreatorUiState.selectedQualityCategoryIndex]

                    selectedCategory.qualities.forEach { qualityKey ->
                        val displayName = ChordUtils.CHORD_QUALITIES[qualityKey] ?: qualityKey
                        val isSelected = chordCreatorUiState.selectedChordQuality == qualityKey

                        Button(
                            onClick = {
                                onPresetSelected(chordCreatorUiState.selectedChordRoot, qualityKey)
                                onPlayPreset(chordCreatorUiState.selectedChordRoot, qualityKey)
                            },
                            shape = RoundedCornerShape(8.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = if (isSelected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceVariant,
                                contentColor = if (isSelected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        ) {
                            Text(displayName.ifBlank { "maj" }, softWrap = false)
                        }
                    }
                }


                if (chordCreatorUiState.selectedInstrument == "piano") {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            IconButton(
                                onClick = { onOctaveChange(-1) },
                                enabled = chordCreatorUiState.keyboardOctave > 0,
                                colors = IconButtonDefaults.iconButtonColors(
                                    containerColor = MaterialTheme.colorScheme.surfaceVariant,
                                    contentColor = MaterialTheme.colorScheme.onPrimary
                                )
                            ) { Icon(Icons.Default.ChevronLeft, "Previous Octave") }
                            Text(
                                "Octave ${chordCreatorUiState.keyboardOctave}",
                                fontFamily = FontFamily.Monospace,
                                style = MaterialTheme.typography.bodySmall
                            )
                            IconButton(
                                onClick = { onOctaveChange(1) },
                                enabled = chordCreatorUiState.keyboardOctave < 7,
                                colors = IconButtonDefaults.iconButtonColors(
                                    containerColor = MaterialTheme.colorScheme.surfaceVariant,
                                    contentColor = MaterialTheme.colorScheme.onPrimary
                                )
                            ) { Icon(Icons.Default.ChevronRight, "Next Octave") }
                        }
                        if (chordCreatorUiState.manualKeys.isNotEmpty()) {
                            Button(
                                onClick = onClearManualKeys,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.surfaceVariant,
                                    contentColor = MaterialTheme.colorScheme.primary
                                ),
                                contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp)
                            ) { Text("Clear") }
                        }
                    }
                    PianoKeyboard(
                        octave = chordCreatorUiState.keyboardOctave,
                        highlightedNotes = displayNotesOnKeyboard,
                        onKeyClick = onToggleManualKey,
                        modifier = Modifier.tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_CUSTOM_EXPLAIN)
                    )
                } else {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_TUNING),
                        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally)
                    ) {
                        OutlinedButton(onClick = { showTuningPopup = true }) { Text("Tuning: ${chordCreatorUiState.guitarTuning.joinToString(" ")}") }
                        OutlinedButton(onClick = { showCapoPopup = true }) { Text(if (chordCreatorUiState.guitarCapo == 0) "No Capo" else "Capo: ${chordCreatorUiState.guitarCapo}") }
                    }
                    Spacer(modifier = Modifier.height(16.dp))
                    GraphicalGuitarFretboard(
                        tab = chordCreatorUiState.currentTab,
                        onTabChange = onTabChange,
                        tuning = chordCreatorUiState.guitarTuning,
                        onTuningChange = onTuningChange,
                        capo = chordCreatorUiState.guitarCapo,
                        fretWindowStart = fretWindowStart,
                        onFretWindowChange = onFretWindowChange,
                        isLeftHanded = isLeftHanded,
                        modifier = Modifier
                            .tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_CUSTOM_EXPLAIN)
                            .tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_CUSTOM_TOGGLE, isSecondary = true)
                    )
                }

                if (showCapoPopup) {
                    CapoSelectionPopup(
                        onDismiss = { showCapoPopup = false },
                        onConfirm = { showCapoPopup = false },
                        currentCapo = chordCreatorUiState.guitarCapo,
                        onCapoChange = { newCapo ->
                            onCapoChange(newCapo)
                            onFretWindowChange((newCapo - 1).coerceAtLeast(0))
                        }
                    )
                }
                if (showTuningPopup) {
                    TuningSelectionPopup(
                        onDismiss = { showTuningPopup = false },
                        currentTuning = chordCreatorUiState.guitarTuning,
                        onTuningChange = onTuningChange,
                        onReset = onResetTuning
                    )
                }
                Spacer(modifier = Modifier.height(16.dp))
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 16.dp, end = 16.dp, bottom = 16.dp, top = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OutlinedTextField(
                    value = customChordName,
                    onValueChange = { customChordName = it },
                    label = { Text("Chord Name") },
                    modifier = Modifier.weight(1f).tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_SAVE, isSecondary = true),
                    singleLine = true
                )
                
                IconToggleButton(
                    checked = isFavorited,
                    onCheckedChange = { 
                        onToggleFavorite(
                            songwritersnotebook.app.data.ChordDefinition(
                                name = customChordName,
                                root = chordCreatorUiState.selectedChordRoot,
                                quality = chordCreatorUiState.selectedChordQuality,
                                guitarTab = chordCreatorUiState.currentTab.map { it.toString() },
                                pianoNotes = chordCreatorUiState.manualKeys,
                                isCustom = chordCreatorUiState.isCustom
                            )
                        ) 
                    },
                    modifier = Modifier.size(56.dp).tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_FAVORITE)
                ) {
                    Icon(
                        if (isFavorited) Icons.Filled.Favorite else Icons.Filled.FavoriteBorder,
                        contentDescription = if (isFavorited) "Remove from favorites" else "Add to favorites",
                        tint = if (isFavorited) Color.Red else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                Button(
                    onClick = { onSave(customChordName) },
                    enabled = customChordName.isNotBlank(),
                    modifier = Modifier.height(56.dp).tutorialTarget(TutorialStep.EDITOR_CREATE_CHORD_SAVE)
                ) {
                    Icon(Icons.Default.Check, "Save Chord")
                }
            }
        }
        TutorialOverlay(isGlobal = false)
    }
}
}

@Composable
fun GraphicalGuitarFretboard(
    tab: List<Any>,
    onTabChange: (List<Any>) -> Unit,
    tuning: List<String>,
    onTuningChange: (Int, Int) -> Unit,
    capo: Int,
    fretWindowStart: Int,
    onFretWindowChange: (Int) -> Unit,
    isLeftHanded: Boolean = false,
    modifier: Modifier = Modifier
) {
    val numFretsToShow = 5
    val primaryColor = MaterialTheme.colorScheme.primary
    val reversedTuning = remember(tuning) { tuning.reversed() }

    val tabNotation = remember(tab, capo) {
        tab.joinToString(" ") { fret ->
            when (fret) {
                is Int -> {
                    if (fret > 0) {
                        (fret - capo).toString()
                    } else {
                        "0"
                    }
                }
                else -> fret.toString()
            }
        }
    }

    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        val layoutDirection = if (isLeftHanded) androidx.compose.ui.unit.LayoutDirection.Rtl else androidx.compose.ui.unit.LayoutDirection.Ltr
        CompositionLocalProvider(androidx.compose.ui.platform.LocalLayoutDirection provides layoutDirection) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                CompositionLocalProvider(androidx.compose.ui.platform.LocalLayoutDirection provides androidx.compose.ui.unit.LayoutDirection.Ltr) {
                    Column(
                        modifier = Modifier.height(180.dp),
                        verticalArrangement = Arrangement.SpaceEvenly,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        reversedTuning.forEach { note ->
                            Text(
                                note.dropLast(1),
                                textAlign = TextAlign.Center,
                                modifier = Modifier.width(24.dp),
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }

                Column(
                    modifier = Modifier
                        .height(180.dp)
                        .padding(horizontal = 4.dp),
                    verticalArrangement = Arrangement.SpaceEvenly,
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    (0..5).forEach { reversedStringIndex ->
                        val actualStringIndex = 5 - reversedStringIndex
                        val fret = tab.getOrNull(actualStringIndex)
                        val isMuted = fret == 'x'

                        Box(
                            modifier = Modifier
                                .size(24.dp)
                                .clickable {
                                    val newTab = tab.toMutableList()
                                    newTab[actualStringIndex] = if (isMuted) 0 else 'x'
                                    onTabChange(newTab)
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            if (isMuted) {
                                Icon(Icons.Default.Close, "Muted", tint = Color.Red)
                            } else {
                                Box(
                                    modifier = Modifier
                                        .size(12.dp)
                                        .border(1.dp, MaterialTheme.colorScheme.onSurfaceVariant, CircleShape)
                                )
                            }
                        }
                    }
                }

                Canvas(modifier = Modifier
                    .weight(1f)
                    .height(180.dp)
                    .pointerInput(tab, capo, isLeftHanded) {
                        detectTapGestures { offset ->
                            if (size.height <= 0 || size.width <= 0) return@detectTapGestures
                            
                            val stringHeight = size.height / 6f
                            val reversedStringIndex = (offset.y / stringHeight).toInt().coerceIn(0, 5)
                            val actualStringIndex = 5 - reversedStringIndex

                            val effectiveX = if (isLeftHanded) size.width - offset.x else offset.x
                            val fretWidth = size.width / numFretsToShow.toFloat()
                            val fretIndex = (effectiveX / fretWidth).toInt().coerceIn(0, numFretsToShow - 1)
                            val fretNumber = fretWindowStart + fretIndex + 1

                            if (fretNumber <= capo) return@detectTapGestures

                            val newTab = tab.toMutableList()
                            newTab[actualStringIndex] = if (tab[actualStringIndex] == fretNumber) 0 else fretNumber
                            onTabChange(newTab)
                        }
                    }
                ) {
                    if (size.width <= 0 || size.height <= 0) return@Canvas
                    
                    val numStrings = 6
                    val stringHeight = size.height / numStrings.toFloat()
                    val fretWidth = size.width / numFretsToShow.toFloat()

                    if (stringHeight.isNaN() || fretWidth.isNaN()) return@Canvas

                    drawRect(color = Color(0xFF525252))
                    (0..numFretsToShow).forEach { fret ->
                        val rawX = fret * fretWidth
                        val x = if (isLeftHanded) size.width - rawX else rawX
                        
                        val stroke = if (fret == 0 && capo == 0) 6f else 2f
                        drawLine(Color.Gray, start = Offset(x, 0f), end = Offset(x, size.height), strokeWidth = stroke)
                    }

                    (0 until numStrings).forEach { string ->
                        val y = (string + 0.5f) * stringHeight
                        drawLine(Color.LightGray, start = Offset(0f, y), end = Offset(size.width, y), strokeWidth = (string * 0.4f) + 1.5f)
                    }

                    if (capo > 0 && capo >= (fretWindowStart + 1) && capo < fretWindowStart + numFretsToShow) {
                        val capoFretPosIndex = capo - fretWindowStart
                        val rawXCenter = (capoFretPosIndex * fretWidth) - (fretWidth / 2f)
                        val xCenter = if (isLeftHanded) size.width - rawXCenter else rawXCenter
                        
                        val barWidth = 12.dp.toPx()
                        drawRoundRect(
                            color = primaryColor.copy(alpha = 0.9f),
                            topLeft = Offset((xCenter - (barWidth / 2)).toFloat(), -4.dp.toPx()),
                            size = Size(barWidth, size.height + 8.dp.toPx()),
                            cornerRadius = CornerRadius(6.dp.toPx())
                        )
                    }

                    val markerFrets = listOf(3, 5, 7, 9, 12, 15, 17, 19, 21)
                    (0 until numFretsToShow).forEach { fretIndex ->
                        val fretNumber = fretWindowStart + fretIndex + 1
                        if (markerFrets.contains(fretNumber)) {
                            val rawX = (fretIndex * fretWidth) + (fretWidth / 2f)
                            val x = if (isLeftHanded) size.width - rawX else rawX
                            
                            val isDoubleDot = fretNumber % 12 == 0
                            if (isDoubleDot) {
                                drawCircle(Color.Gray, radius = 4.dp.toPx(), center = Offset(x, size.height * 0.25f))
                                drawCircle(Color.Gray, radius = 4.dp.toPx(), center = Offset(x, size.height * 0.75f))
                            } else {
                                drawCircle(Color.Gray, radius = 4.dp.toPx(), center = Offset(x, size.height / 2f))
                            }
                        }
                    }

                    val reversedTabForDrawing = tab.reversed()
                    reversedTabForDrawing.forEachIndexed { reversedStringIndex, fret ->
                        val y = (reversedStringIndex + 0.5f) * stringHeight
                        if (fret is Int && fret > capo) {
                            if (fret > fretWindowStart && fret <= fretWindowStart + numFretsToShow) {
                                val fretPosIndex = fret - fretWindowStart - 1
                                val rawX = (fretPosIndex * fretWidth) + (fretWidth / 2f)
                                val x = if (isLeftHanded) size.width - rawX else rawX
                                
                                drawCircle(color = primaryColor, radius = stringHeight * 0.35f, center = Offset(x, y))
                            }
                        }
                    }
                }

                CompositionLocalProvider(androidx.compose.ui.platform.LocalLayoutDirection provides androidx.compose.ui.unit.LayoutDirection.Ltr) {
                    Column(
                        modifier = Modifier
                            .height(180.dp)
                            .padding(start = 8.dp),
                        verticalArrangement = Arrangement.SpaceEvenly,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        (0..5).forEach { reversedStringIndex ->
                            val actualStringIndex = 5 - reversedStringIndex
                            val playedNote = getNoteForFret(
                                actualStringIndex,
                                tab.getOrElse(actualStringIndex) { 'x' },
                                tuning,
                                capo
                            )
                            Text(
                                playedNote,
                                textAlign = TextAlign.Center,
                                modifier = Modifier.width(32.dp),
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontSize = 12.sp
                            )
                        }
                    }
                }
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 8.dp, start = 4.dp, end = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            if (isLeftHanded) {
                IconButton(onClick = { onFretWindowChange((fretWindowStart + 1).coerceAtMost(18)) }, enabled = fretWindowStart < 18) {
                    Icon(Icons.Default.ChevronLeft, "Shift Left")
                }
            } else {
                IconButton(onClick = { onFretWindowChange((fretWindowStart - 1).coerceAtLeast(0)) }, enabled = fretWindowStart > 0) {
                    Icon(Icons.Default.ChevronLeft, "Shift Left")
                }
            }
            
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(
                    "Fret: ${fretWindowStart + 1}",
                    fontFamily = FontFamily.Monospace,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                // Middle buttons removed for simpler navigation or logic
            }
            
            if (isLeftHanded) {
                IconButton(onClick = { onFretWindowChange((fretWindowStart - 1).coerceAtLeast(0)) }, enabled = fretWindowStart > 0) {
                    Icon(Icons.Default.ChevronRight, "Shift Right")
                }
            } else {
                IconButton(onClick = { onFretWindowChange((fretWindowStart + 1).coerceAtMost(18)) }, enabled = fretWindowStart < 18, modifier = Modifier.size(24.dp)) {
                    Icon(Icons.Default.ChevronRight, "Shift Right")
                }
            }
            
            Text(
                text = tabNotation,
                fontFamily = FontFamily.Monospace,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier
                    .background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(4.dp))
                    .padding(horizontal = 12.dp, vertical = 6.dp)
            )
        }
        Spacer(Modifier.size(48.dp))
    }
}

@Composable
fun PianoKeyboard(
    octave: Int,
    highlightedNotes: List<String>,
    onKeyClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(100.dp)
    ) {
        Row(modifier = Modifier.fillMaxSize()) {
            ChordUtils.PIANO_KEYS_IN_OCTAVE.filter { !it.second }.forEach { (noteName, _) ->
                val fullNoteName = "$noteName$octave"
                val isHighlighted = highlightedNotes.contains(fullNoteName)
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                        .border(1.dp, MaterialTheme.colorScheme.outline)
                        .background(if (isHighlighted) MaterialTheme.colorScheme.primary.copy(alpha = 0.8f) else MaterialTheme.colorScheme.onPrimary)
                        .clickable { onKeyClick(fullNoteName) }
                )
            }
        }
        Row(modifier = Modifier.fillMaxSize()) {
            Spacer(modifier = Modifier.weight(0.6f))
            BlackKey(note = "Db$octave", highlighted = highlightedNotes.contains("Db$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(0.8f))
            BlackKey(note = "Eb$octave", highlighted = highlightedNotes.contains("Eb$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(1.6f))
            BlackKey(note = "Gb$octave", highlighted = highlightedNotes.contains("Gb$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(0.8f))
            BlackKey(note = "Ab$octave", highlighted = highlightedNotes.contains("Ab$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(0.8f))
            BlackKey(note = "Bb$octave", highlighted = highlightedNotes.contains("Bb$octave"), onClick = onKeyClick)
            Spacer(modifier = Modifier.weight(0.6f))
        }
    }
}

@Composable
fun RowScope.BlackKey(note: String, highlighted: Boolean, onClick: (String) -> Unit) {
    Box(
        modifier = Modifier
            .weight(0.8f)
            .fillMaxHeight(0.6f)
            .padding(horizontal = 2.dp)
            .clip(RoundedCornerShape(bottomStart = 4.dp, bottomEnd = 4.dp))
            .background(if (highlighted) MaterialTheme.colorScheme.primary else Color.Black)
            .border(1.dp, Color.Black)
            .clickable { onClick(note) }
    )
}

private fun getNoteForFret(stringIndex: Int, fret: Any, tuning: List<String>, capo: Int): String {
    if (fret !is Int || fret < 0) return "---"
    val openNote = tuning.getOrNull(stringIndex) ?: return "---"
    val openMidi = ChordUtils.noteNameToMidi(openNote) ?: return "---"
    val finalMidi = if (fret == 0) openMidi + capo else openMidi + fret
    return ChordUtils.midiToNoteName(finalMidi)
}

@Composable
private fun TuningSelectionPopup(
    onDismiss: () -> Unit,
    currentTuning: List<String>,
    onTuningChange: (Int, Int) -> Unit,
    onReset: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Edit Tuning") },
        text = {
            Column {
                currentTuning.forEachIndexed { index, note ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("String ${6 - index}: $note")
                        Row {
                            IconButton(onClick = { onTuningChange(index, -1) }) { Icon(Icons.Default.Remove, "Tune Down") }
                            IconButton(onClick = { onTuningChange(index, 1) }) { Icon(Icons.Default.Add, "Tune Up") }
                        }
                    }
                }
            }
        },
        dismissButton = {
            TextButton(onClick = onReset) { Text("Reset to Standard") }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("Done") }
        }
    )
}

@Composable
private fun CapoSelectionPopup(
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
    currentCapo: Int,
    onCapoChange: (Int) -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Set Capo") },
        text = {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
                modifier = Modifier.fillMaxWidth()
            ) {
                IconButton(onClick = { onCapoChange(currentCapo - 1) }, enabled = currentCapo > 0) {
                    Icon(Icons.Default.Remove, "Decrease Capo")
                }
                Text(
                    text = if (currentCapo == 0) "No Capo" else "Fret $currentCapo",
                    style = MaterialTheme.typography.headlineSmall,
                    modifier = Modifier.width(120.dp),
                    textAlign = TextAlign.Center
                )
                IconButton(onClick = { onCapoChange(currentCapo + 1) }, enabled = currentCapo < 12) {
                    Icon(Icons.Default.Add, "Increase Capo")
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onConfirm) { Text("Done") }
        }
    )
}
